// Groups & users — the people-first landing surface (mockups/access-
// preview-v6.html "people" view). Two tabs (Groups / Users) sharing the
// same filter+table shell. Wire format follows what the BFF returns from
// /api/groups and /api/users; both endpoints are tolerated in either
// "items" or "groups"/"users" envelope shape (the BFF was renamed mid-
// rework and we don't want this page tied to a specific snapshot).

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, api, useApiErrorHandler } from "@/api";
import { Modal, useToast } from "@/components";
import { ruleClient } from "@/data/clients/ruleClient";
import { groupClient } from "@/data/clients/groupClient";
import { userClient } from "@/data/clients/userClient";
import { useCapabilities } from "@/role";
import type { Rule } from "@/types/rule";
import { isRecentlyActive, relativeTime, roleClass } from "./format";
import "../AdminShared.css";

// ---------------------------------------------------------------------------
// Wire types — tolerant. /api/groups and /api/users are still in flux.
// ---------------------------------------------------------------------------

interface ApiGroup {
  group_name: string;
  group_id?: string;
  display_name?: string;
  member_count?: number;
  synced_at?: string;
}

interface ApiUser {
  alias: string;
  ring_user_id?: string;
  display_name?: string;
  group_names?: string[];
  /** Highest-priority role surfaced by the BFF when grants exist. */
  effective_role_id?: string;
  /** Optional cached grant summary; if absent we render "— none —". */
  role_ids?: string[];
  /** Locations covered by the user's grants. "ALL" or a list of IDs. */
  effective_location_scope?: { mode: "ALL" | "ALLOW" | "DENY"; ids: string[] };
  location_count?: number;
  last_seen_at?: string;
  synced_at?: string;
  /** "no access" badge: backend is authoritative when it sets this. */
  has_access?: boolean;
}

interface GroupsResponse {
  items?: ApiGroup[];
  groups?: ApiGroup[];
}

interface UsersResponse {
  items?: ApiUser[];
  users?: ApiUser[];
}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

type Tab = "groups" | "users";
type ActiveIn = "30" | "7" | "1" | "all";

interface GroupRow {
  group: ApiGroup;
  rules: Rule[];
  /** Best (most-permissive scope, then first by display order) rule for the chip. */
  primaryRule: Rule | null;
}

/** Pick the most-permissive rule for the chip column.
 *  Order: any rule with location_scope.mode === "ALL" wins; otherwise the
 *  one with the largest non-DENY id list; finally, the first by name. */
function pickPrimaryRule(rules: Rule[]): Rule | null {
  if (rules.length === 0) return null;
  const allRule = rules.find((r) => r.location_scope.mode === "ALL");
  if (allRule) return allRule;
  const allowSorted = rules
    .filter((r) => r.location_scope.mode !== "DENY")
    .sort((a, b) => b.location_scope.ids.length - a.location_scope.ids.length);
  if (allowSorted.length > 0) return allowSorted[0] ?? null;
  return rules[0] ?? null;
}

/** Avatar initial used in the .avatar-sm circles. */
function initial(s: string): string {
  return (s || "?").charAt(0).toUpperCase();
}

/** Pick an unwrapped array from an envelope that uses either "items" or
 *  the legacy plural-noun key. */
function unwrap<T>(resp: { items?: T[] }, legacyKey: "groups" | "users"): T[] {
  if (Array.isArray(resp.items)) return resp.items;
  const r = resp as Record<string, unknown>;
  const legacy = r[legacyKey];
  return Array.isArray(legacy) ? (legacy as T[]) : [];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PeoplePage(): JSX.Element {
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();
  const toast = useToast();
  const caps = useCapabilities();

  const [tab, setTab] = useState<Tab>("groups");
  // Delete confirmation state — single in-flight modal for both kinds.
  const [deleteTarget, setDeleteTarget] = useState<
    | { kind: "user"; alias: string }
    | { kind: "group"; groupId: string; groupName: string }
    | null
  >(null);
  const [deleting, setDeleting] = useState(false);

  const [groups, setGroups] = useState<ApiGroup[]>([]);
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);

  // Groups-tab filters
  const [groupSearch, setGroupSearch] = useState("");
  const [groupStatus, setGroupStatus] = useState<"" | "bound" | "unrouted">("");
  const [groupRoleFilter, setGroupRoleFilter] = useState("");

  // Users-tab filters
  const [userSearch, setUserSearch] = useState("");
  const [userGroupFilter, setUserGroupFilter] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("");
  // "Active in" select — visual only per spec (the wire model doesn't
  // surface a uniform last_seen_at across users yet).
  const [activeIn, setActiveIn] = useState<ActiveIn>("30");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsResp, usersResp, rulesResp] = await Promise.all([
        api
          .get<GroupsResponse>("/api/groups")
          .catch((err: unknown) => {
            // Empty groups is a valid state — only surface the toast for
            // unexpected errors. 404 means "no groups yet".
            if (err instanceof ApiError && err.status === 404) {
              return { items: [] } as GroupsResponse;
            }
            throw err;
          }),
        api
          .get<UsersResponse>("/api/users")
          .catch((err: unknown) => {
            if (err instanceof ApiError && err.status === 404) {
              return { items: [] } as UsersResponse;
            }
            throw err;
          }),
        ruleClient.list({ limit: 200 }),
      ]);
      setGroups(unwrap<ApiGroup>(groupsResp, "groups"));
      setUsers(unwrap<ApiUser>(usersResp, "users"));
      setRules(rulesResp.items);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Load people" });
      }
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteTarget === null) return;
    setDeleting(true);
    try {
      if (deleteTarget.kind === "user") {
        const res = await userClient.delete(deleteTarget.alias);
        toast.success({
          title: `User '${deleteTarget.alias}' deleted`,
          description:
            res.memberships_removed > 0
              ? `Cascaded ${res.memberships_removed} group membership${
                  res.memberships_removed === 1 ? "" : "s"
                }`
              : undefined,
        });
      } else {
        const res = await groupClient.delete(deleteTarget.groupId);
        toast.success({
          title: `Group '${deleteTarget.groupName}' deleted`,
          description: `Removed ${res.rows_removed} membership row${res.rows_removed === 1 ? "" : "s"}`,
        });
      }
      setDeleteTarget(null);
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Delete" });
      }
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, toast, handleApiError, load]);

  // -------------------------------------------------------------------------
  // Cross-reference: rules → group attribution
  // -------------------------------------------------------------------------
  const groupRows = useMemo<GroupRow[]>(() => {
    return groups.map((g) => {
      const matched = rules.filter(
        (r) => r.group_match?.group_name === g.group_name,
      );
      return {
        group: g,
        rules: matched,
        primaryRule: pickPrimaryRule(matched),
      };
    });
  }, [groups, rules]);

  const unroutedCount = useMemo(
    () => groupRows.filter((row) => row.rules.length === 0).length,
    [groupRows],
  );

  // Most recent SCIM sync time across groups+users (the page caption is
  // a single "last sync ago" so we surface the freshest signal).
  const lastSyncIso = useMemo<string | null>(() => {
    let max: number | null = null;
    const consider = (iso: string | undefined): void => {
      if (!iso) return;
      const t = Date.parse(iso);
      if (Number.isNaN(t)) return;
      if (max === null || t > max) max = t;
    };
    for (const g of groups) consider(g.synced_at);
    for (const u of users) consider(u.synced_at);
    return max === null ? null : new Date(max).toISOString();
  }, [groups, users]);

  // Distinct role IDs that any rule binds to — drives the Groups-tab Role
  // dropdown so the operator only sees options that would actually filter.
  const distinctRoleIds = useMemo(() => {
    const set = new Set<string>();
    for (const r of rules) set.add(r.role_id);
    return Array.from(set).sort();
  }, [rules]);

  // -------------------------------------------------------------------------
  // Filtered group rows
  // -------------------------------------------------------------------------
  const filteredGroupRows = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    return groupRows.filter((row) => {
      const isUnrouted = row.rules.length === 0;
      if (groupStatus === "bound" && isUnrouted) return false;
      if (groupStatus === "unrouted" && !isUnrouted) return false;
      if (groupRoleFilter !== "") {
        if (!row.rules.some((r) => r.role_id === groupRoleFilter)) return false;
      }
      if (q.length === 0) return true;
      const name = (row.group.display_name ?? row.group.group_name).toLowerCase();
      const id = (row.group.group_id ?? row.group.group_name).toLowerCase();
      return (
        name.includes(q) ||
        id.includes(q) ||
        row.group.group_name.toLowerCase().includes(q)
      );
    });
  }, [groupRows, groupSearch, groupStatus, groupRoleFilter]);

  // -------------------------------------------------------------------------
  // Filtered user rows + dropdown options
  // -------------------------------------------------------------------------
  const distinctUserGroups = useMemo(() => {
    const set = new Set<string>();
    for (const u of users) {
      for (const g of u.group_names ?? []) set.add(g);
    }
    return Array.from(set).sort();
  }, [users]);

  const distinctUserRoles = useMemo(() => {
    const set = new Set<string>();
    for (const u of users) {
      if (u.effective_role_id) set.add(u.effective_role_id);
      for (const r of u.role_ids ?? []) set.add(r);
    }
    return Array.from(set).sort();
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    return users.filter((u) => {
      if (userGroupFilter !== "") {
        if (!(u.group_names ?? []).includes(userGroupFilter)) return false;
      }
      if (userRoleFilter !== "") {
        const roles = new Set([
          ...(u.role_ids ?? []),
          ...(u.effective_role_id ? [u.effective_role_id] : []),
        ]);
        if (!roles.has(userRoleFilter)) return false;
      }
      if (q.length === 0) return true;
      const name = (u.display_name ?? u.alias).toLowerCase();
      const ringId = (u.ring_user_id ?? "").toLowerCase();
      return (
        u.alias.toLowerCase().includes(q) ||
        name.includes(q) ||
        ringId.includes(q)
      );
    });
  }, [users, userSearch, userGroupFilter, userRoleFilter]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  const totalGroups = groups.length;
  const totalUsers = users.length;
  const lastSyncRel = lastSyncIso ? relativeTime(lastSyncIso) : null;

  return (
    <section className="admin-page">
      <header className="v6-page-header">
        <div className="row">
          <h1>Groups &amp; users</h1>
          {(caps.canManageUsers || caps.canManageGroups) && (
            <div className="actions" style={{ display: "flex", gap: 8 }}>
              {caps.canManageUsers && (
                <button
                  type="button"
                  className="v6-btn ghost"
                  onClick={() => navigate("/admin/people/users/new")}
                >
                  + New user
                </button>
              )}
              {caps.canManageGroups && (
                <button
                  type="button"
                  className="v6-btn primary"
                  onClick={() => navigate("/admin/people/groups/new")}
                >
                  + New group
                </button>
              )}
            </div>
          )}
        </div>
        <div className="caption">
          <b>
            {totalUsers} user{totalUsers === 1 ? "" : "s"}
          </b>{" "}
          across{" "}
          <b>
            {totalGroups} group{totalGroups === 1 ? "" : "s"}
          </b>
          . Provisioned automatically from your IdP via SCIM
          {lastSyncRel ? <> — last sync {lastSyncRel}.</> : <>.</>}
        </div>
      </header>

      <div className="v6-people-tabs" role="tablist" aria-label="People tabs">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "groups"}
          className={tab === "groups" ? "active" : ""}
          onClick={() => setTab("groups")}
        >
          Groups <span className="cnt">{totalGroups}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "users"}
          className={tab === "users" ? "active" : ""}
          onClick={() => setTab("users")}
        >
          Users <span className="cnt">{totalUsers}</span>
        </button>
      </div>

      {tab === "groups" ? (
        <GroupsPane
          loading={loading}
          search={groupSearch}
          onSearchChange={setGroupSearch}
          status={groupStatus}
          onStatusChange={setGroupStatus}
          roleFilter={groupRoleFilter}
          onRoleFilterChange={setGroupRoleFilter}
          distinctRoleIds={distinctRoleIds}
          unroutedCount={unroutedCount}
          rows={filteredGroupRows}
          onOpen={(name) => navigate(`/admin/people/groups/${encodeURIComponent(name)}`)}
          onAddRule={() => navigate("/admin/rules/new")}
          onEditGroup={(g) =>
            navigate(`/admin/people/groups/${encodeURIComponent(g.group_id ?? g.group_name)}/edit`)
          }
          onDeleteGroup={(g) =>
            setDeleteTarget({
              kind: "group",
              groupId: g.group_id ?? g.group_name,
              groupName: g.display_name ?? g.group_name,
            })
          }
          canManageGroups={caps.canManageGroups}
          totalGroupCount={groupRows.length}
        />
      ) : (
        <UsersPane
          loading={loading}
          search={userSearch}
          onSearchChange={setUserSearch}
          groupFilter={userGroupFilter}
          onGroupFilterChange={setUserGroupFilter}
          roleFilter={userRoleFilter}
          onRoleFilterChange={setUserRoleFilter}
          activeIn={activeIn}
          onActiveInChange={setActiveIn}
          distinctGroups={distinctUserGroups}
          distinctRoles={distinctUserRoles}
          rows={filteredUsers}
          totalUserCount={users.length}
          rules={rules}
          onTrace={(alias) =>
            navigate(`/admin/trace?alias=${encodeURIComponent(alias)}`)
          }
          onEditUser={(alias) =>
            navigate(`/admin/people/users/${encodeURIComponent(alias)}/edit`)
          }
          onDeleteUser={(alias) => setDeleteTarget({ kind: "user", alias })}
          canManageUsers={caps.canManageUsers}
        />
      )}

      <Modal
        open={deleteTarget !== null}
        onClose={() => (deleting ? undefined : setDeleteTarget(null))}
        variant="confirm"
        title={
          deleteTarget?.kind === "user"
            ? `Delete user '${deleteTarget.alias}'?`
            : deleteTarget?.kind === "group"
            ? `Delete group '${deleteTarget.groupName}'?`
            : ""
        }
        description={
          deleteTarget?.kind === "user"
            ? "This removes the identity row and cascades to every group the user belongs to. Schema-equivalent to SCIM deprovisioning."
            : "This removes every membership row in the group. Schema-equivalent to SCIM removing every member."
        }
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="v6-btn ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="v6-btn danger"
              onClick={() => void handleConfirmDelete()}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        }
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Groups pane
// ---------------------------------------------------------------------------

interface GroupsPaneProps {
  loading: boolean;
  search: string;
  onSearchChange: (v: string) => void;
  status: "" | "bound" | "unrouted";
  onStatusChange: (v: "" | "bound" | "unrouted") => void;
  roleFilter: string;
  onRoleFilterChange: (v: string) => void;
  distinctRoleIds: string[];
  unroutedCount: number;
  rows: GroupRow[];
  totalGroupCount: number;
  onOpen: (groupName: string) => void;
  onAddRule: () => void;
  onEditGroup: (g: ApiGroup) => void;
  onDeleteGroup: (g: ApiGroup) => void;
  canManageGroups: boolean;
}

function GroupsPane({
  loading,
  search,
  onSearchChange,
  status,
  onStatusChange,
  roleFilter,
  onRoleFilterChange,
  distinctRoleIds,
  unroutedCount,
  rows,
  totalGroupCount,
  onOpen,
  onAddRule,
  onEditGroup,
  onDeleteGroup,
  canManageGroups,
}: GroupsPaneProps): JSX.Element {
  return (
    <div>
      <div className="v6-filter-row">
        <div className="search-wrap">
          <span className="ico" aria-hidden="true">⌕</span>
          <input
            type="search"
            placeholder="Search groups by name or ID…"
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onSearchChange(e.target.value)
            }
            aria-label="Search groups"
          />
        </div>
        <select
          value={status}
          onChange={(e) =>
            onStatusChange(e.target.value as "" | "bound" | "unrouted")
          }
          aria-label="Filter groups by status"
        >
          <option value="">Status: any</option>
          <option value="bound">Rule-bound</option>
          <option value="unrouted">Unrouted</option>
        </select>
        <select
          value={roleFilter}
          onChange={(e) => onRoleFilterChange(e.target.value)}
          aria-label="Filter groups by role"
        >
          <option value="">Role: any</option>
          {distinctRoleIds.map((id) => (
            <option key={id} value={id}>
              {id.toUpperCase()}
            </option>
          ))}
        </select>
        <span className="count">
          {loading
            ? "Loading…"
            : `${rows.length} of ${totalGroupCount} group${totalGroupCount === 1 ? "" : "s"}`}
        </span>
      </div>

      {unroutedCount > 0 && (
        <div className="v6-unrouted-banner" role="status">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2L1 21h22zm1 14h-2v-2h2zm0-4h-2V8h2z" />
          </svg>
          <div className="ub-text">
            <b>
              {unroutedCount} group{unroutedCount === 1 ? "" : "s"} ha
              {unroutedCount === 1 ? "s" : "ve"} no rule
            </b>{" "}
            — {unroutedCount === 1 ? "its" : "their"} members are signed in
            but can&apos;t access anything.
          </div>
          <button
            type="button"
            className="v6-btn primary xs"
            onClick={onAddRule}
          >
            Add a rule
          </button>
        </div>
      )}

      <table className="v6-table groups-table">
        <thead>
          <tr>
            <th>Group</th>
            <th>Role &amp; scope</th>
            <th>Rules</th>
            <th>Members</th>
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <SkeletonRows columns={5} />
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="admin-muted" style={{ padding: 24 }}>
                {totalGroupCount === 0
                  ? "No groups synced yet. Provision groups in your IdP — they'll appear here automatically."
                  : "No groups match your filters."}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <GroupRow
                key={row.group.group_name}
                row={row}
                onOpen={onOpen}
                onAddRule={onAddRule}
                onEdit={() => onEditGroup(row.group)}
                onDelete={() => onDeleteGroup(row.group)}
                canManage={canManageGroups}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

interface GroupRowProps {
  row: GroupRow;
  onOpen: (groupName: string) => void;
  onAddRule: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canManage: boolean;
}

function GroupRow({ row, onOpen, onAddRule, onEdit, onDelete, canManage }: GroupRowProps): JSX.Element {
  const { group, rules, primaryRule } = row;
  const isUnrouted = rules.length === 0;
  const displayName = group.display_name ?? group.group_name;
  const id = group.group_id ?? group.group_name;
  const memberCount = group.member_count ?? 0;

  // Scope text for the chip column. We don't know tenant-wide site count
  // here, so the "all sites" branch reads from the rule's scope.ids length
  // when ALL is set we say "all sites" without a number.
  let scopeText = "";
  if (primaryRule) {
    if (primaryRule.location_scope.mode === "ALL") {
      scopeText = "at all sites";
    } else if (primaryRule.location_scope.ids.length === 1) {
      scopeText = `at ${primaryRule.location_scope.ids[0]}`;
    } else {
      scopeText = `at ${primaryRule.location_scope.ids.length} sites`;
    }
  }

  return (
    <tr
      className={`clickable${isUnrouted ? " row-unrouted" : ""}`}
      onClick={() => onOpen(group.group_name)}
    >
      <td className="alias">
        <div className="alias-stack">
          <b>
            {displayName}
            {isUnrouted && <span className="alias-warn">unrouted</span>}
          </b>
          <span className="alias-id">{id}</span>
        </div>
      </td>
      <td>
        {primaryRule ? (
          <>
            <span className={`v6-role-chip ${roleClass(primaryRule.role_id)}`}>
              {primaryRule.role_id.toUpperCase()}
            </span>
            <span
              style={{
                fontSize: 12,
                color: "var(--ring-neutral-300)",
                marginLeft: 6,
              }}
            >
              {scopeText}
            </span>
          </>
        ) : (
          <span
            style={{
              color: "var(--ring-caution-text)",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            No role — group can&apos;t see anything
          </span>
        )}
      </td>
      <td>
        <b>{rules.length}</b>
      </td>
      <td>{memberCount}</td>
      <td className="row-actions">
        <div style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          {canManage && (
            <>
              <button
                type="button"
                className="v6-btn ghost xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                Edit
              </button>
              <button
                type="button"
                className="v6-btn ghost xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                style={{ color: "var(--ring-danger-text, #ff6b6b)" }}
              >
                Delete
              </button>
            </>
          )}
          {isUnrouted ? (
            <button
              type="button"
              className="v6-btn primary xs"
              onClick={(e) => {
                e.stopPropagation();
                onAddRule();
              }}
            >
              Add a rule
            </button>
          ) : (
            <span style={{ fontSize: 12, color: "var(--ring-neutral-300)" }}>
              Open →
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Users pane
// ---------------------------------------------------------------------------

interface UsersPaneProps {
  loading: boolean;
  search: string;
  onSearchChange: (v: string) => void;
  groupFilter: string;
  onGroupFilterChange: (v: string) => void;
  roleFilter: string;
  onRoleFilterChange: (v: string) => void;
  activeIn: ActiveIn;
  onActiveInChange: (v: ActiveIn) => void;
  distinctGroups: string[];
  distinctRoles: string[];
  rows: ApiUser[];
  totalUserCount: number;
  /** Tenant-wide rules; UserRow uses these to derive the effective role
   *  + has-access flag when the BFF response doesn't carry them. */
  rules: Rule[];
  onTrace: (alias: string) => void;
  onEditUser: (alias: string) => void;
  onDeleteUser: (alias: string) => void;
  canManageUsers: boolean;
}

function UsersPane({
  loading,
  search,
  onSearchChange,
  groupFilter,
  onGroupFilterChange,
  roleFilter,
  onRoleFilterChange,
  activeIn,
  onActiveInChange,
  distinctGroups,
  distinctRoles,
  rows,
  totalUserCount,
  rules,
  onTrace,
  onEditUser,
  onDeleteUser,
  canManageUsers,
}: UsersPaneProps): JSX.Element {
  return (
    <div>
      <div className="v6-filter-row">
        <div className="search-wrap">
          <span className="ico" aria-hidden="true">⌕</span>
          <input
            type="search"
            placeholder="Search users by alias or name…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search users"
          />
        </div>
        <select
          value={groupFilter}
          onChange={(e) => onGroupFilterChange(e.target.value)}
          aria-label="Filter users by group"
        >
          <option value="">Group: any</option>
          {distinctGroups.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
        <select
          value={roleFilter}
          onChange={(e) => onRoleFilterChange(e.target.value)}
          aria-label="Filter users by role"
        >
          <option value="">Role: any</option>
          {distinctRoles.map((r) => (
            <option key={r} value={r}>
              {r.toUpperCase()}
            </option>
          ))}
        </select>
        <select
          value={activeIn}
          onChange={(e) => onActiveInChange(e.target.value as ActiveIn)}
          aria-label="Filter users by activity window"
        >
          <option value="30">Active in: 30 days</option>
          <option value="7">7 days</option>
          <option value="1">24 hours</option>
          <option value="all">All-time</option>
        </select>
        <span className="count">
          {loading
            ? "Loading…"
            : `${rows.length} of ${totalUserCount} user${totalUserCount === 1 ? "" : "s"}`}
        </span>
      </div>

      <table className="v6-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Groups</th>
            <th>Effective role</th>
            <th>Where</th>
            <th>Last seen</th>
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <SkeletonRows columns={6} />
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={6} className="admin-muted" style={{ padding: 24 }}>
                {totalUserCount === 0
                  ? "No users synced yet. Provision users in your IdP — they'll appear here automatically."
                  : "No users match your filters."}
              </td>
            </tr>
          ) : (
            rows.map((u) => (
              <UserRow
                key={u.alias}
                user={u}
                rules={rules}
                onTrace={onTrace}
                onEdit={() => onEditUser(u.alias)}
                onDelete={() => onDeleteUser(u.alias)}
                canManage={canManageUsers}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

interface UserRowProps {
  user: ApiUser;
  /** Tenant rules — used to derive the effective role + has-access flag
   *  when /api/users doesn't carry them (which is the BFF's current
   *  shape). */
  rules: Rule[];
  onTrace: (alias: string) => void;
  onEdit: () => void;
  onDelete: () => void;
  canManage: boolean;
}

function UserRow({ user, rules, onTrace, onEdit, onDelete, canManage }: UserRowProps): JSX.Element {
  const groupNames = user.group_names ?? [];
  const ringId = user.ring_user_id ?? "—";
  // BFF authoritative-when-set; otherwise derive from group→rule binding so
  // a user in a group bound to any enabled rule shows that rule's role.
  // Without this fallback every user shows "no access" even when their
  // group is wired to Fleet Administrator.
  const derivedRule =
    user.effective_role_id || user.role_ids?.[0]
      ? null
      : rules.find(
          (r) =>
            r.enabled &&
            r.group_match != null &&
            groupNames.includes(r.group_match.group_name),
        ) ?? null;
  const role =
    user.effective_role_id ??
    user.role_ids?.[0] ??
    derivedRule?.role_id;
  const hasAnyRole = !!role;
  const hasAccess = user.has_access ?? hasAnyRole;
  const recent = isRecentlyActive(user.last_seen_at);

  let where: string;
  if (user.effective_location_scope) {
    const s = user.effective_location_scope;
    if (s.mode === "ALL") {
      where =
        user.location_count && user.location_count > 0
          ? `All ${user.location_count} sites`
          : "All sites";
    } else if (s.ids.length === 1) {
      where = s.ids[0] ?? "—";
    } else if (s.ids.length === 0) {
      where = "—";
    } else {
      where = `${s.ids.length} sites`;
    }
  } else {
    where = hasAnyRole ? "—" : "—";
  }

  return (
    <tr>
      <td className="alias">
        <span className="avatar-sm">{initial(user.alias)}</span>
        <div className="alias-stack">
          <b>{user.display_name ?? user.alias}</b>
          <span className="alias-id">{ringId}</span>
        </div>
      </td>
      <td>
        {groupNames.length === 0 ? (
          <span style={{ color: "var(--ring-neutral-300)" }}>—</span>
        ) : (
          <>
            {groupNames.join(", ")}
            {!hasAccess && (
              <span className="alias-warn" title="Group has no role bound">
                no access
              </span>
            )}
          </>
        )}
      </td>
      <td>
        {hasAnyRole ? (
          <span className={`v6-role-chip ${roleClass(role)}`}>
            {role.toUpperCase()}
          </span>
        ) : (
          <span className="v6-role-chip r-empty">— none —</span>
        )}
      </td>
      <td style={{ color: "var(--ring-neutral-300)", fontSize: 13 }}>
        {where}
      </td>
      <td style={{ color: "var(--ring-neutral-300)" }}>
        {recent ? (
          <>
            <span className="dot-online" aria-hidden="true" />
            <span
              style={{
                color: "var(--ring-positive-500)",
                fontWeight: 500,
              }}
            >
              {relativeTime(user.last_seen_at)}
            </span>
          </>
        ) : (
          relativeTime(user.last_seen_at)
        )}
      </td>
      <td className="row-actions">
        <div style={{ display: "inline-flex", gap: 6 }}>
          {canManage && (
            <>
              <button
                type="button"
                className="v6-btn ghost xs"
                onClick={onEdit}
              >
                Edit
              </button>
              <button
                type="button"
                className="v6-btn ghost xs"
                onClick={onDelete}
                style={{ color: "var(--ring-danger-text, #ff6b6b)" }}
              >
                Delete
              </button>
            </>
          )}
          <button
            type="button"
            className="v6-btn ghost xs"
            onClick={() => onTrace(user.alias)}
          >
            Trace →
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Skeleton rows (matches RulesListPage's "three placeholder rows" pattern)
// ---------------------------------------------------------------------------

function SkeletonRows({ columns }: { columns: number }): JSX.Element {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <tr key={i} aria-hidden="true">
          {Array.from({ length: columns }, (_, c) => (
            <td key={c}>
              <div
                style={{
                  height: 14,
                  background: "var(--ring-tertiary-bg)",
                  borderRadius: 4,
                  width: c === 0 ? "60%" : c === columns - 1 ? "30%" : "40%",
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
